import React from 'react';
import ReactDOM from 'react-dom/client';
import {
	Route,
	RouterProvider,
	createBrowserRouter,
	createRoutesFromElements,
} from 'react-router-dom';

import AppRoot from './AppRoot';
import RootFrame from './pages/RootFrame';
import Home from './pages/Home';
import PrintPage from './pages/PrintPage';
import PageLayoutEditorPage from './components/layouts/PageLayoutEditorPage';
import Page404 from './pages/Page404';
import RouteError from './pages/RouteError';
import Collections from './pages/Collections';
import RecentlyDeletedCollections from './pages/RecentlyDeletedCollections';
import Profile from './pages/Profile';
import History from './pages/History';
import Drafts from './pages/Drafts';
// A third-party publisher's own packages (keepr-api KPR-1331).
import Publishing from './pages/Publishing';
import PublishingPackagePage from './pages/PublishingPackagePage';
import TransferOfferPage from './pages/TransferOfferPage';
import Login from './pages/Login';
import Register from './pages/Register';
import Logout from './pages/Logout';
import CollectionsLayout from './pages/CollectionsLayout';
import { SHELL_LAYOUT, SHELL_LAYOUT_SAVE } from './config/routeHandles';
import ShortRecordRoute from './pages/ShortRecordRoute';
import CollectionHome from './pages/CollectionHome';
import CollectionSearchPage from './pages/CollectionSearchPage';
import CollectionItemsPage from './pages/CollectionItemsPage';
import AccountChangePassword from './pages/AccountChangePassword';
import AccountNotifications from './pages/AccountNotifications';
import AccountTags from './pages/AccountTags';
import TagPage from './pages/TagPage';
import CombinedListPage from './pages/CombinedListPage';
import ReportsPage from './pages/ReportsPage';
import ChartPage from './pages/ChartPage';
import ChartBuilderPage from './pages/ChartBuilderPage';
import DashboardPage from './pages/DashboardPage';
import NewDashboardPage from './pages/NewDashboardPage';
import OAuthAuthorize from './pages/OAuthAuthorize';
import ConnectAssistant from './pages/ConnectAssistant';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ShortLinkPage from './pages/ShortLinkPage';
import PublicFormPage from './pages/PublicFormPage';
import Status from './pages/Status';
import EmailPreferences from './pages/EmailPreferences';
import ConsumerHealthData from './pages/ConsumerHealthData';
import PublicShell from './components/common/PublicShell';
import RequireSession from './components/common/RequireSession';
import ViewCards from './components/cards/ViewCards';
import CardDefinitionForm from './components/cards/CardDefinitionForm';
import ElementSetPage from './components/elementSets/ElementSetPage';
import Discover from './pages/Discover';
import PackagePage from './pages/PackagePage';
import GlobalCollectionRedirect from './pages/GlobalCollectionRedirect';
import CollectionForm from './components/collections/CollectionForm';
import ItemForm, { NewItemFormRoute } from './components/items/ItemForm';
import { MultiAddPageRoute } from './components/items/MultiAddPage';
import MultiAddEditorPage from './components/items/MultiAddEditorPage';
import ChangeCardPage from './components/changeCard/ChangeCardPage';
import SpreadsheetImportPage from './components/import/SpreadsheetImportPage';
import { NotificationEditorRoute } from './components/notifications/NotificationEditorPage';
import adminRoutes from './components/admin/adminRoutes';
import docsRoutes from './components/docs/docsRoutes';
import { ThemeProvider } from './contexts/ThemeContext';
import { installFieldGuard } from './utils/fieldAssist';
import { shell } from './platform';
import { installExternalLinks } from './platform/externalLinks';
import { createShortUrlWindow, installShortUrls, wrapCreateHref } from './utils/shortUrlWindow';
import { api } from './api/client';
import { hydrateSession } from './utils/session';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'antd/dist/reset.css';
import './css/themes.css';
import './css/tokens.css';
import './css/main.css';
import './css/shell.css';
import './css/components.css';

/**
 * The route table. A data router (createBrowserRouter below) built from the
 * same <Route> JSX the app always declared, so the tree still reads top to
 * bottom and docs/ARCHITECTURE.md's route table is checked against it
 * (src/__tests__/docsRoutes.test.js reads this file as text).
 *
 * A data router rather than <BrowserRouter> because only a data router can
 * hold a navigation: the unsaved-changes guard (hooks/useDirtyForm.js,
 * contexts/UnsavedChangesContext.js) is a `useBlocker`. There are still no
 * loaders and no actions — a route renders at once and fetches after.
 *
 * Exported for the route smoke test (src/__tests__/appRoutes.test.js).
 */
export const appRoutes = createRoutesFromElements(
	// The root: no path, so it matches every URL and never remounts. Its
	// element is the provider stack (src/AppRoot.js), which a data router's
	// hooks need to sit under. Its errorElement is the app's error page, for
	// whatever the shell itself (or a page outside it) throws while
	// rendering; a page inside the shell has its own, below.
	<Route element={<AppRoot />} errorElement={<RouteError />}>
		{/* Paper. Outside the shell on purpose: a print route has no
		    rail and no context bar, and what is on screen is what
		    comes out of the printer. Behind the session guard like
		    every other protected page (see the shell's group below). */}
		<Route element={<RequireSession />}>
			<Route
				path='collections/:collectionId/print/:cardId/:layoutId'
				element={<PrintPage />}
			/>
		</Route>

		{/* The compact-link resolver. Outside the shell on purpose:
		    a share code — a shared view, or an intake form —
		    must render for a visitor with no account, and the
		    shell's rail and palette would fire authenticated
		    reads around it. PublicShell is the wordmark and the
		    page, nothing else. */}
		<Route element={<PublicShell />}>
			<Route path='s/:code' element={<ShortLinkPage />} />
			{/* A permanent public form's address (KPR-513): readable,
			    printed on a sign, for a visitor with no account — so
			    outside the shell, like a share code. Two segments:
			    `/f/:code` (one) is a multi add's short link, inside
			    the shell, for a signed-in person. */}
			<Route path='f/:collectionSlug/:formSlug' element={<PublicFormPage />} />
		</Route>

		{/* The public SITE (KPE-008): the same public shell, with Docs,
		    Sign in and Sign up in its header. The landing page at `/` is
		    its first page, but it is not a route here: `/` is shared with
		    the signed-in Home, and RootFrame (below) picks between them by
		    session. Contact (KPR-317), Terms and Privacy (KPR-318) and
		    Status (KPR-320) join this group, and their footer links turn on
		    in src/config/publicSite.js in the same change. */}
		<Route element={<PublicShell site />}>
			{/* "Manage notification emails" in the foot of every mail:
			    session-less and off-only, and opened in whatever browser
			    the mail was read in — so outside the shell, like a share
			    code. */}
			<Route path='email-preferences' element={<EmailPreferences />} />
			{/* keepr's consumer health data policy (KPR-478): public and
			    signed out, opened from the health-package consent step in a
			    new tab and from the public footer. The legal pages'
			    switch is src/config/legal.js. */}
			<Route path='consumer-health-data' element={<ConsumerHealthData />} />
			{/* keepr's status page (KPR-320): versions and health, from
			    GET /api/status. Public and signed out. */}
			<Route path='status' element={<Status />} />
		</Route>

		{/* The /admin portal (KPR-309): its own UI, opened in its own
		    browser tab from the rail, so OUTSIDE the app shell — no rail,
		    no context bar of the app's, no palette; AdminLayout draws the
		    portal's own header. Behind the session guard (signed out → sign
		    in and back) and, inside the fragment, the admin guard, which
		    answers before the lazy admin chunk is ever requested. Plus the
		    redirects from the retired flat /admin-* paths. The fragment
		    lives in components/admin/adminRoutes.js so the routing tests
		    exercise the real config. */}
		<Route element={<RequireSession />}>
			{adminRoutes()}
		</Route>

		{/* The app shell (Layout) — except at `/` with no session, where
		    RootFrame shows the public landing page instead (KPR-316). */}
		<Route path='/' element={<RootFrame />}>
			{/* The shell's error boundary: a page that throws while
			    rendering shows the app's error page (src/pages/RouteError.js)
			    inside the rail and context bar, so the person can go on
			    elsewhere. Pathless, so it adds nothing to any URL. */}
			<Route errorElement={<RouteError />}>

				<Route index element={<Home />} />

				<Route path='*' element={<Page404 />} />

				<Route path='login' element={<Login />} />
				<Route path='register' element={<Register />} />
				<Route path='logout' element={<Logout />} />

				{/* Landing pages for the links the API mails out. Public,
				    like login: the recipient may not be signed in. */}
				<Route path='verify-email' element={<VerifyEmail />} />
				<Route path='forgot-password' element={<ForgotPassword />} />
				<Route path='reset-password' element={<ResetPassword />} />

				{/* The /docs portal: user guides for everyone, and — behind
				    the admin guard — the atlases, change log and line counts. */}
				{docsRoutes()}

				{/* Everything below needs a session. The guard is a pathless
				    layout route, so a route added here is protected by
				    default: no session (no token, or an expired one) goes
				    to sign in before the page renders. What stays outside
				    is public — Home, the 404 page, the auth and landing
				    pages above, and the documentation portal (whose admin
				    half is RequireAdmin's). src/__tests__/appRoutes.test.js
				    fails on a non-public route of the ARCHITECTURE table
				    that is not in here. */}
				<Route element={<RequireSession />}>
					{/* Short URLs (KPE-002): a record's /<letter>/<code>. A code
					    this browser knows never gets here — the router's window
					    reads it as the long path (utils/shortUrlWindow.js); an
					    unknown one is resolved and replaced by the long path,
					    the address bar keeping the short form. Letters held for
					    later kinds are listed in docs/plans/short-record-urls.md. */}
					<Route path='c/:code/*' element={<ShortRecordRoute />} />
					<Route path='i/:code' element={<ShortRecordRoute />} />
					<Route path='d/:code' element={<ShortRecordRoute />} />
					<Route path='t/:code' element={<ShortRecordRoute />} />
					<Route path='e/:code' element={<ShortRecordRoute />} />
					<Route path='f/:code' element={<ShortRecordRoute />} />

					<Route path='collections'>
						<Route index element={<Collections />} />
						<Route path='new' element={<CollectionForm />} />
						{/* The caller's own deleted collections (delete audit F5) — a literal segment, so it wins over :collectionId. */}
						<Route path='deleted' element={<RecentlyDeletedCollections />} />

						{/* The collection shell. A child marked SHELL_LAYOUT is drawn
						    under the layout's header (crumb, badge, tabs, actions);
						    SHELL_LAYOUT_SAVE is the same where the collection form's
						    Save changes is the primary. A child with no handle draws
						    its own bar — so a new one can never stack two headers
						    (config/routeHandles.js; collectionShellRoutes.test.js
						    renders every child and counts the breadcrumbs). */}
						<Route path=':collectionId' element={<CollectionsLayout />}>
							{/* The collection's own home — what the context bar's Home button lands on. */}
							<Route index element={<CollectionHome />} handle={SHELL_LAYOUT} />
							<Route path='items' element={<CollectionItemsPage />} handle={SHELL_LAYOUT} />
							{/* The grouped search of the collection: each card once, its matching items under it (KPR-355). */}
							<Route path='search' element={<CollectionSearchPage />} handle={SHELL_LAYOUT} />
							<Route path='items/new/:cardId' element={<NewItemFormRoute />} handle={SHELL_LAYOUT} />
							<Route path='items/:itemId' element={<ItemForm />} handle={SHELL_LAYOUT} />
							<Route path='multi-add/new' element={<MultiAddEditorPage />} />
							{/* The notification editor. Keyed on what it seeds
							    from (shape, card, tier, the key it overrides),
							    so a second launch mounts a fresh form rather
							    than reusing one whose latches already fired. */}
							<Route path='notifications/new' element={<NotificationEditorRoute />} />
							<Route path='notifications/:notificationId/edit' element={<NotificationEditorRoute />} />
							{/* The Cards tab (KPR-1281, KPE-039): the collection's Card Library and
							    element sets, for everyone who reads the collection. */}
							<Route path='cards' element={<CollectionForm section='cards' />} handle={SHELL_LAYOUT_SAVE} />
							{/* A collection card's page (KPR-1282): its home and editor inside the
							    shell, Cards current, the sections a left sub-nav. A card this
							    collection does not own is sent to its owner (or, global, to /cards). */}
							<Route path='cards/:cardId' element={<CardDefinitionForm />} handle={SHELL_LAYOUT_SAVE} />
							{/* The page-layout canvas. A card's, so the card is in the path; inside
							    the shell with Cards current, Cancel and Save in its actions (KPR-1287). */}
							<Route path='cards/:cardId/layouts/new' element={<PageLayoutEditorPage />} handle={SHELL_LAYOUT_SAVE} />
							<Route path='cards/:cardId/layouts/:layoutId/edit' element={<PageLayoutEditorPage />} handle={SHELL_LAYOUT_SAVE} />
							<Route path='multi-add/:formId' element={<MultiAddPageRoute />} />
							<Route path='multi-add/:formId/edit' element={<MultiAddEditorPage />} />
							{/* Change card's mapping utility (KPE-028, KPR-707): the selection rides the query (?card=&ids= | q= | all=1). */}
							<Route path='change-card' element={<ChangeCardPage />} />
							{/* Import a spreadsheet (KPR-1899): the card rides the query (?card=). */}
							<Route path='import' element={<SpreadsheetImportPage />} />
							<Route path='form' element={<CollectionForm />} handle={SHELL_LAYOUT_SAVE} />
							{/* Reports (charts A5a, plan §6.1): the collection's charts,
							    under the collection's own bar. A chart has its own page
							    and bar (§6.3), and so has the builder (charts A5b, §6.2)
							    that makes and edits one; `charts/new` is a literal
							    segment, so it wins over `charts/:chartId`. */}
							<Route path='reports' element={<ReportsPage />} handle={SHELL_LAYOUT} />
							<Route path='charts/new' element={<ChartBuilderPage />} />
							<Route path='charts/:chartId' element={<ChartPage />} />
							<Route path='charts/:chartId/edit' element={<ChartBuilderPage />} />
							{/* Dashboards (charts A6, §6.5): a named arrangement of
							    charts with its own bar; `dashboards/new` is a literal
							    segment, so it wins over `dashboards/:dashboardId`. */}
							<Route path='dashboards/new' element={<NewDashboardPage />} />
							<Route path='dashboards/:dashboardId' element={<DashboardPage />} />
						</Route>
						{/* The legacy events pages (`:collectionId/events`,
						    `…/events/:eventId`) were retired with the API's
						    events feed (KPR-1033); an old bookmark is the
						    shell's 404. */}
					</Route>

					<Route path='cards'>
						<Route index element={<ViewCards />} />
						<Route path=':cardId' element={<CardDefinitionForm />} />
					</Route>
					{/* Element sets: the card page's shape for the bundles a card attaches. */}
					<Route path='element-sets/:setId' element={<ElementSetPage />} />

					<Route path='discover'>
						<Route index element={<Discover />} />
						<Route path=':packageKey' element={<PackagePage />} />
						{/* Another publisher's package (keepr-api KPR-358): its key is
						    `<publisher>/<name>`. `global/…` ranks first, and no publisher
						    may hold the key `global` (the API reserves it). */}
						<Route path=':publisher/:name' element={<PackagePage />} />
						<Route path='global/:collectionId' element={<GlobalCollectionRedirect />} />
					</Route>
					{/* Publishing (keepr-api KPR-1331): a third-party publisher's own
					    accounts make, publish, rebuild and retire its packages, each
					    listed version through review. The account menu offers it to
					    those accounts; the API refuses anyone else. */}
					<Route path='publishing'>
						<Route index element={<Publishing />} />
						<Route path='packages/:packageId' element={<PublishingPackagePage />} />
					</Route>

					{/* The tag page (docs/plans/tags.md §5.2, T5): everything carrying
					    one tag, across every collection — any kind of tag. */}
					<Route path='tags/:tagId' element={<TagPage />} />
					{/* Combined lists (KPE-034, KPR-1022): one card across every collection the person lists. */}
					<Route path='lists/new' element={<CombinedListPage />} />
					<Route path='lists/:listId' element={<CombinedListPage />} />

					<Route path='profile' element={<Profile />} />
					<Route path='history' element={<History />} />
					<Route path='drafts' element={<Drafts />} />
					{/* A collection offered to a new owner (KPR-743): the notice's and the email's Accept and Decline land here. */}
					<Route path='transfers/:offerId' element={<TransferOfferPage />} />
					<Route path='account'>
						<Route index element={<Profile />} />
						<Route path='change-password' element={<AccountChangePassword />} />
						{/* Everything that can reach this person, from every
						    collection, plus the three delivery masters. */}
						<Route path='notifications' element={<AccountNotifications />} />
						{/* My private tags (docs/plans/tags.md §5.2 Account › Tags). */}
						<Route path='tags' element={<AccountTags />} />
						<Route path='connect' element={<ConnectAssistant />} />
					</Route>
					{/* The OAuth consent screen — an assistant sends the person here. */}
					<Route path='oauth/authorize' element={<OAuthAuthorize />} />
				</Route>

			</Route>
		</Route>
	</Route>
);

// Only in the browser: a test imports `appRoutes` from here without
// mounting the app (jsdom has no #root).
const container = document.getElementById('root');
if (container) {
	// The short-URL boundary (KPE-002): the address bar shows a record's
	// /<letter>/<code>, the router works on the long path — see
	// utils/shortUrlWindow.js. Every href the router makes is short too, and
	// every API answer teaches the dictionary the codes it carries.
	const router = wrapCreateHref(createBrowserRouter(appRoutes, { window: createShortUrlWindow(window) }));
	installShortUrls({ api });
	// Before the first render: password managers only where a field asks for
	// one, and no autocorrect in a dropdown's search (utils/fieldAssist.js).
	installFieldGuard();
	// In the phone app a WebView has no tabs: an outside link goes to the platform
	// (a no-op in a browser; platform/externalLinks.js).
	installExternalLinks();
	// A keepr address opened from the phone app is a page of the app, not a
	// load of the whole bundle (nothing on the web).
	shell.routeWith(path => router.navigate(path));
	// The session first (KPR-1669): the token is read ONCE from the
	// platform's secure store, which only answers asynchronously (the
	// Keychain in the app; localStorage, in a microtask, on the web), and the
	// first render waits for it. Rendering before it would draw a signed-out
	// frame for a signed-in person, and let a per-account cache start on no
	// account. It never rejects; a store that does not answer within its
	// bound boots signed out (utils/session.js).
	hydrateSession().then(() => ReactDOM.createRoot(container).render(
		<React.StrictMode>
			{/* Outside the router and above everything: it renders the antd
			    ConfigProvider (and App), so the palette is in place before any
			    antd component mounts. ThemeSync, in AppRoot, feeds it the saved
			    preferences once they load. */}
			<ThemeProvider>
				<RouterProvider router={router} />
			</ThemeProvider>
		</React.StrictMode>
	)).then(() => { requestAnimationFrame(() => shell.ready()); });
}
